import React, { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { Container } from 'reactstrap';
import { skipToken } from '@reduxjs/toolkit/query';

import { AppointmentConflictTypes } from '~constants/maps';

import { faExclamationTriangle } from '~components/icon';
import { Row, TableView } from '~components/table-view';

import conflictsColumns from '~weekly-planner/forms/conflicts/list';

import { useGetConflictsQuery } from '~weekly-planner/actions/appointments/conflicts';

import { clear, updateParams } from '~weekly-planner/reducers/appointments/conflicts';

import { selectDerivedWeekRange, selectShowSystemUsers } from '~weekly-planner/selectors';
import { selectAll, selectCount, selectOptions, selectParams } from '~weekly-planner/selectors/appointments/conflicts';

import ConflictsViewMenu from './ConflictsViewMenu';

interface ComponentProps {
  isOpen: boolean;
}

const Conflicts: React.FC<ComponentProps> = React.memo(({ isOpen }) => {
  const dispatch = useAppDispatch();
  const params = useAppSelector(selectParams);
  const appointments = useAppSelector(selectAll);
  const { conflictTime, conflictTravel, conflictPreference } = useAppSelector(selectOptions);
  const count = useAppSelector(selectCount);
  const showSystemUsers = useAppSelector(selectShowSystemUsers);
  const showSystemUsersParam = showSystemUsers ? { show_system_users: true } : {};

  const derivedWeekRange = useAppSelector(selectDerivedWeekRange);
  const { week_start } = derivedWeekRange;

  const { limit } = params;

  const { PREFERENCE_CLASH, TIME_CLASH, TRAVEL_CLASH } = AppointmentConflictTypes;

  // RTK Query Args
  const queryArgs = useMemo(
    () => ({
      limit,
      week_start: params?.week_start,
      ...showSystemUsersParam,
    }),
    [params, showSystemUsers],
  );

  // RTK Query Hook
  const { isFetching } = useGetConflictsQuery(params?.week_start && isOpen ? queryArgs : skipToken, {
    refetchOnMountOrArgChange: true,
  });

  const onLoadMore = () => {
    const newLimit = limit + 30;
    dispatch(updateParams({ ...params, limit: newLimit }));
  };

  useEffect(() => {
    if (isOpen && week_start) {
      dispatch(updateParams({ week_start, limit: 30, ...showSystemUsersParam }));
    }
  }, [isOpen, showSystemUsers, week_start]);

  useEffect(() => {
    return () => {
      dispatch(clear([]));
    };
  }, []);

  const filteredAppointments = appointments.filter(
    (appt) =>
      (conflictTime && appt.conflict_type === TIME_CLASH) ||
      (conflictTravel && appt.conflict_type === TRAVEL_CLASH) ||
      (conflictPreference && appt.conflict_type === PREFERENCE_CLASH),
  );

  const columns = conflictsColumns();
  return (
    <div className="conflict-list">
      <Container className="inner-content limited">
        <div className="nav">
          <ConflictsViewMenu />
        </div>
        <TableView
          id="conflicts"
          data={filteredAppointments as Row[]}
          columns={columns}
          readonly
          striped
          isLoading={isFetching}
          allowPagination={true}
          onLoadMore={onLoadMore}
          totalCount={count}
          emptyIcon={faExclamationTriangle}
          emptyMessage="No conflicts found"
        />
      </Container>
    </div>
  );
});

Conflicts.displayName = 'Conflicts';
export default Conflicts;
